/**
 * A partner's Pricing → Rate card leaf (TMMPDM-0086; links to the one Rate cards page since TMMPDM-0089). The adopted card is the one whose
 * `adoptingResellers` contains this reseller, and the granted cards are those whose `grants`
 * contain this reseller. Both are filters of ids the server sent, not a verdict that the
 * reseller can sell. "Can't sell" stays the server's `NO_RATE_CARD` elsewhere.
 * There is no per-adoption date on this view, so this leaf has no "Non-compliant since" chip.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy } from '@/api/errors';
import type { CustomerRateCardView } from '@/api/tmm/types';
import { EmptyState, ErrorState, SectionCard, SubmitButton, useNotify } from '@/components/page';
import { Amount } from '@/components/ui';
import { useCopy } from '@/hooks/useCopy';
import { currentVersion, versionChipLabel } from './customerRateCardModel';

export function PartnerCustomerRateCardPanel({ resellerId }: { resellerId: number }) {
  const { t } = useCopy();
  const notify = useNotify();
  const [cards, setCards] = useState<CustomerRateCardView[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listCustomerRateCards()
      .then((list) => {
        if (cancelled) return;
        setCards(list.rateCards);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCards(null);
        setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const title = t('partners.tab.priceCard', 'Rate card');
  if (error) {
    return (
      <ErrorState
        error={error}
        message={t('rateCard.list.loadError', 'Could not load rate cards.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }
  if (cards === null) {
    return <Alert severity="info">{t('common.loading', 'Loading…')}</Alert>;
  }

  const adopted = cards.find((card) => card.adoptingResellers.includes(resellerId));
  const granted = cards.filter((card) => card.grants.includes(resellerId));
  const choices = granted.filter((card) => card.id !== adopted?.id);
  const selected =
    picked !== null && choices.some((card) => card.id === picked)
      ? picked
      : (choices[0]?.id ?? null);
  const selectedCard = choices.find((card) => card.id === selected);
  const adoptedVersion = adopted ? currentVersion(adopted) : undefined;

  const close = () => {
    if (busy) return;
    setOpen(false);
    setDialogError(null);
  };

  const adopt = async () => {
    if (selected === null) return;
    const name = selectedCard?.name ?? '';
    setBusy(true);
    setDialogError(null);
    try {
      await adminApi.adoptCustomerRateCard(resellerId, selected);
    } catch (err) {
      setDialogError(
        err instanceof ApiError && String(err.code) === 'RATE_CARD_NOT_ACTIVE'
          ? t('customerRateCards.adopt.notPublished', 'This card has no published version.')
          : errorCopy(err, t, t('customerRateCards.adopt.error', "Couldn't adopt this rate card.")),
      );
      setBusy(false);
      return;
    }
    try {
      const list = await adminApi.listCustomerRateCards();
      setCards(list.rateCards);
      setError(null);
    } catch (err) {
      setError(err);
    }
    setOpen(false);
    notify.success(t('customerRateCards.toast.adopted', '{{name}} is the adopted card.', { name }));
    setBusy(false);
  };

  return (
    <SectionCard id="partner-rate-card-title" title={title}>
      {adopted ? (
        <Stack spacing={0.5}>
          <Link component={RouterLink} to={`/rate-cards/${adopted.id}`}>
            {adopted.name}
          </Link>
          <Typography>
            {adoptedVersion
              ? `${versionChipLabel(adoptedVersion.versionNo, t)} · `
              : `${t('customerRateCards.noCurrent', 'No published version')} · `}
            {adoptedVersion ? (
              <>
                <Amount minor={adoptedVersion.baseMinor} currency={adopted.currency} />{' '}
                {t('partners.perMonth', 'per month')}
              </>
            ) : null}
          </Typography>
        </Stack>
      ) : (
        <EmptyState title={t('customerRateCards.adopt.empty', 'No card adopted')} />
      )}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle1" component="h3">
          {t('customerRateCards.tabs.grants', 'Grants')}
        </Typography>
        <Button onClick={() => setOpen(true)} disabled={choices.length === 0}>
          {t('customerRateCards.actions.adopt', 'Adopt…')}
        </Button>
      </Stack>
      {granted.length === 0 ? (
        <Typography>
          {t('customerRateCards.adopt.noneGranted', 'No rate card is granted to this reseller.')}
        </Typography>
      ) : (
        <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
          {granted.map((card) => {
            const version = currentVersion(card);
            return (
              <li key={card.id}>
                <Link component={RouterLink} to={`/rate-cards/${card.id}`}>
                  {card.name}
                </Link>
                {version ? ` · ${versionChipLabel(version.versionNo, t)}` : ''}
                {card.id === adopted?.id
                  ? ` · ${t('customerRateCards.grants.adopted', 'Adopts this card')}`
                  : ''}
              </li>
            );
          })}
        </Stack>
      )}
      <Dialog open={open} onClose={close} aria-labelledby="adopt-customer-card-title">
        <DialogTitle id="adopt-customer-card-title">
          {t('customerRateCards.adopt.title', 'Adopt rate card')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {t('customerRateCards.adopt.consequence', 'Adopt {{name}} for this reseller?', {
              name: selectedCard?.name ?? '',
            })}
          </Typography>
          <TextField
            select
            fullWidth
            label={t('rateCard.singular', 'Rate card')}
            value={selected === null ? '' : String(selected)}
            onChange={(event) => setPicked(Number(event.target.value))}
          >
            {choices.map((card) => (
              <MenuItem key={card.id} value={String(card.id)}>
                {card.name}
              </MenuItem>
            ))}
          </TextField>
          {dialogError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <SubmitButton busy={busy} disabled={selected === null} onClick={() => void adopt()}>
            {t('customerRateCards.adopt.confirm', 'Adopt')}
          </SubmitButton>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
}
